import { el } from "../core/dom";
import { createRequiredMarker } from "../core/required-marker";
import { part } from "../appearance/parts";
import { DEFAULT_PURPOSE_SECTION_HEADING } from "../appearance/constants";
import { PRODUCT_POLICIES_LABEL } from "../constants";
import { dataElementKey } from "../modal/consentSelection";
import { renderProductPolicyLine } from "../modal/product-policy-line";
import { shouldShowNoticePolicyLink } from "../modal/productPolicies";
import { ASSISTED_IDS, FOCUS_KEY_ATTRIBUTE, FOCUS_KEYS } from "./constants";
import { chevronIcon } from "./icons";
import { hasBlockingRequiredElement, isBlockingRequiredElement } from "./selection";
import { assistedStyles } from "./styles";
import type { PurposeItemData } from "../api/types";
import type { AssistedViewContext } from "./types";

const linkAttrs = (ctx: AssistedViewContext, id: string, href: string) => ({
  id,
  href,
  target: "_blank",
  rel: "noopener noreferrer",
  style: { ...ctx.styles.link, color: ctx.linkColor },
});

const textStyle = (ctx: AssistedViewContext) => ({
  ...ctx.styles.privacyText,
  ...ctx.responsive.privacyText,
});

export const renderNoticeText = (ctx: AssistedViewContext): HTMLElement => {
  const { content, t, ts } = ctx;
  const anchorText = t(
    "privacy_policy_anchor_text",
    content.privacy_policy_anchor_text || "Privacy Policy"
  );
  const link = el(
    "a",
    {
      ...linkAttrs(ctx, "privacy-policy-link", content.privacy_policy_url || "#"),
      "aria-label": `${anchorText} (${ts("opensInNewTab")})`,
    },
    anchorText
  );
  // The notice-level policy hides once every product names one of its own.
  const showNoticePolicyLink = shouldShowNoticePolicyLink(content.products, ctx.productPolicyLinks);
  return el(
    "p",
    { ...part("notice-text"), style: textStyle(ctx) },
    el("span", { id: "privacy-notice-text" }, t("notice_text", content.notice_text)),
    showNoticePolicyLink && el("br"),
    showNoticePolicyLink &&
      el(
        "span",
        null,
        el(
          "span",
          { id: "privacy-policy-text" },
          t("privacy_policy_prefix_text", content.privacy_policy_prefix_text || "")
        ),
        " ",
        link
      )
  );
};

export const renderPurposeHeading = (ctx: AssistedViewContext): HTMLElement =>
  el(
    "h2",
    {
      id: "purpose-section-heading",
      style: { ...ctx.styles.subTitle, ...ctx.responsive.subTitle },
    },
    ctx.t(
      "purpose_section_heading",
      ctx.content.purpose_section_heading || DEFAULT_PURPOSE_SECTION_HEADING
    )
  );

const renderPurpose = (
  ctx: AssistedViewContext,
  purpose: PurposeItemData,
  locked: boolean
): HTMLElement => {
  const { state, t, ts, handlers, styles, responsive } = ctx;
  const isExpanded = !state.collapsedPurposes[purpose.uuid];
  const name = t("purposes.name", purpose.name, purpose.uuid);
  const description = t("purposes.description", purpose.description, purpose.uuid);
  const dataElements = purpose.data_elements.filter((element) => element.enabled !== false);
  const containerId = `${ASSISTED_IDS.purposeContainerPrefix}${purpose.uuid}`;

  const title = el("h2", {
    id: `purpose-name-${purpose.uuid}`,
    ...part("purpose-title"),
    style: { ...styles.optionTitle, ...responsive.optionTitle },
  });
  title.textContent = name;
  if (hasBlockingRequiredElement(purpose)) {
    title.appendChild(createRequiredMarker(ts("required")));
  }

  const textContainer = el("div", { style: styles.optionTextContainer }, title);
  if (description) {
    textContainer.appendChild(
      el(
        "h3",
        {
          id: `purpose-description-${purpose.uuid}`,
          style: { ...styles.optionDescription, ...responsive.optionDescription },
        },
        description
      )
    );
  }

  const collapse = el(
    "div",
    {
      style: styles.optionLeft,
      role: "button",
      tabIndex: "0",
      "aria-expanded": String(isExpanded),
      "aria-controls": containerId,
      "aria-label": isExpanded ? ts("collapseDetails", { name }) : ts("expandDetails", { name }),
      [FOCUS_KEY_ATTRIBUTE]: FOCUS_KEYS.collapse(purpose.uuid),
      onClick: () => handlers.onPurposeCollapse(purpose.uuid),
      onKeydown: (event: KeyboardEvent) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handlers.onPurposeCollapse(purpose.uuid);
        }
      },
    },
    el(
      "div",
      { style: { display: "flex", alignItems: "center", justifyContent: "center", marginLeft: "5px" } },
      chevronIcon(isExpanded, ctx.look.chevronColor)
    ),
    textContainer
  );

  const checkbox = el("input", {
    className: "redacto-checkbox-large",
    type: "checkbox",
    checked: Boolean(state.selectedPurposes[purpose.uuid]),
    disabled: locked,
    "aria-label": ts("selectAllFor", { name }),
    [FOCUS_KEY_ATTRIBUTE]: FOCUS_KEYS.purpose(purpose.uuid),
    onChange: () => handlers.onPurposeToggle(purpose.uuid),
  });

  const elementsContainer = el("div", {
    id: containerId,
    style: {
      ...styles.dataElementsContainer,
      display: isExpanded && dataElements.length > 0 ? "flex" : "none",
    },
  });
  for (const element of dataElements) {
    const elementName = t("data_elements.name", element.name, element.uuid);
    const label = el("span", {
      id: `data-element-${purpose.uuid}-${element.uuid}`,
      style: { ...styles.dataElementText, ...responsive.dataElementText },
    });
    label.textContent = elementName;
    if (isBlockingRequiredElement(element)) {
      label.appendChild(createRequiredMarker(ts("required")));
    }
    const elementCheckbox = el("input", {
      className: "redacto-checkbox-small",
      type: "checkbox",
      checked: Boolean(state.selectedDataElements[dataElementKey(purpose.uuid, element.uuid)]),
      disabled: locked,
      "aria-label": element.required
        ? ts("selectElementRequired", { name: elementName })
        : ts("selectElement", { name: elementName }),
      [FOCUS_KEY_ATTRIBUTE]: FOCUS_KEYS.element(purpose.uuid, element.uuid),
      onChange: () => handlers.onDataElementToggle(purpose.uuid, element.uuid),
    });
    elementsContainer.appendChild(
      el("div", { ...part("data-element"), style: styles.dataElementItem }, label, elementCheckbox)
    );
  }

  return el(
    "div",
    null,
    el("div", { ...part("purpose"), style: styles.optionItem }, collapse, checkbox),
    elementsContainer
  );
};

export const renderPurposes = (ctx: AssistedViewContext, locked: boolean): HTMLElement => {
  const container = el("div", {
    ...part("purposes"),
    style: { ...ctx.styles.optionsContainer, ...(locked ? assistedStyles.lockedOptions : {}) },
    "aria-disabled": locked ? "true" : null,
  });
  // In the order the visitor sees them: the saved order, product by product.
  for (const purpose of ctx.displayedPurposes) {
    container.appendChild(renderPurpose(ctx, purpose, locked));
  }
  return container;
};

export const renderDpoFooter = (ctx: AssistedViewContext): HTMLElement | null => {
  const { content, t, tDpo, styles, responsive, productPolicyLinks } = ctx;
  const dpo = content.dpo_info;
  // The admin's additional text shows with or without a privacy-center link;
  // the link follows it only when the notice has one.
  const hasPrivacyCenterLine = Boolean(content.privacy_center_url || content.additional_text);
  if (!hasPrivacyCenterLine && !dpo && productPolicyLinks.length === 0) return null;

  const paragraph = el("p", { style: { margin: 0, opacity: "0.6" } });
  const linkStyle = { ...styles.link, color: ctx.linkColor };

  if (hasPrivacyCenterLine) {
    paragraph.appendChild(
      el(
        "span",
        { id: "additional-text" },
        t("additional_text", content.additional_text || "Manage or withdraw consent anytime at")
      )
    );
    if (content.privacy_center_url) {
      paragraph.appendChild(document.createTextNode(" "));
      paragraph.appendChild(
        el(
          "a",
          linkAttrs(ctx, "privacy-center-link", content.privacy_center_url),
          t("privacy_center_anchor_text", content.privacy_center_anchor_text || "Privacy Center")
        )
      );
      paragraph.appendChild(document.createTextNode("."));
    }
  }

  if (productPolicyLinks.length > 0) {
    if (hasPrivacyCenterLine) paragraph.appendChild(el("br"));
    // Each policy is named the way its product is: translated.
    paragraph.appendChild(
      renderProductPolicyLine({
        label: t("product_privacy_policies_label", PRODUCT_POLICIES_LABEL),
        links: productPolicyLinks.map((link) => ({
          ...link,
          name: t("products.name", link.name, link.uuid),
        })),
        linkStyle,
      })
    );
  }

  if (dpo) {
    if (hasPrivacyCenterLine || productPolicyLinks.length > 0) paragraph.appendChild(el("br"));
    paragraph.appendChild(
      el("span", { id: "dpo-grievance-text" }, tDpo("grievance_text", dpo.grievance_text))
    );
    paragraph.appendChild(document.createTextNode(" "));
    if (dpo.grievance_url) {
      paragraph.appendChild(
        el(
          "a",
          linkAttrs(ctx, "dpo-grievance-link", dpo.grievance_url),
          tDpo("grievance_anchor_text", dpo.grievance_anchor_text || "click here")
        )
      );
    }
    paragraph.appendChild(document.createTextNode(" "));
    if (dpo.grievance_email) {
      paragraph.appendChild(
        el(
          "span",
          { id: "dpo-grievance-email-connector" },
          tDpo("grievance_email_connector_text", dpo.grievance_email_connector_text || "or email to")
        )
      );
      paragraph.appendChild(document.createTextNode(" "));
      paragraph.appendChild(
        el(
          "a",
          {
            id: "dpo-grievance-email",
            href: `mailto:${dpo.grievance_email}`,
            style: linkStyle,
          },
          dpo.grievance_email
        )
      );
    }
    paragraph.appendChild(el("br"));
    paragraph.appendChild(
      el("span", { id: "dpo-dp-board-text" }, tDpo("dp_board_text", dpo.dp_board_text))
    );
    paragraph.appendChild(document.createTextNode(" "));
    if (dpo.dp_board_url) {
      paragraph.appendChild(
        el(
          "a",
          linkAttrs(ctx, "dpo-dp-board-link", dpo.dp_board_url),
          tDpo("dp_board_anchor_text", dpo.dp_board_anchor_text || "click here")
        )
      );
    }
    paragraph.appendChild(el("br"));
    paragraph.appendChild(el("span", { id: "dpo-dpo-text" }, tDpo("dpo_text", dpo.dpo_text)));
    paragraph.appendChild(document.createTextNode(" "));
    const dpoAnchor = tDpo("dpo_anchor_text", dpo.dpo_anchor_text || "Click here");
    if (dpo.dpo_email) {
      paragraph.appendChild(
        el(
          "a",
          {
            id: "dpo-dpo-link",
            href: `mailto:${dpo.dpo_email}`,
            style: linkStyle,
          },
          dpoAnchor
        )
      );
    } else if (dpo.dpo_url) {
      paragraph.appendChild(el("a", linkAttrs(ctx, "dpo-dpo-link", dpo.dpo_url), dpoAnchor));
    }
  }

  return el(
    "div",
    {
      ...part("dpo"),
      style: {
        ...styles.privacyText,
        ...responsive.privacyText,
        fontSize: responsive.dpoText.fontSize,
        lineHeight: "1.4",
        borderTop: "1px solid rgba(152, 162, 179, 0.3)",
        paddingTop: "12px",
        marginTop: "12px",
      },
    },
    paragraph
  );
};
